// src/pages/client/components/OrderList.tsx
import { useState } from 'react';
import type { Order } from '@/domains/client/services/client.service';

interface OrderListProps {
    orders: Order[];
    loading: boolean;
    onNewOrder: () => void;
    onTrackOrder: (order: Order) => void;
}

export default function OrderList({ orders, loading, onNewOrder, onTrackOrder }: OrderListProps) {
    const [searchQuery, setSearchQuery] = useState('');

    const filteredOrders = orders.filter(order =>
        order.code_suivi.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.adresse_livraison.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="text-2xl font-bold text-gray-900">Mes Commandes</h2>
                <button
                    onClick={onNewOrder}
                    className="px-5 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-medium flex items-center gap-2 transition-colors"
                >
                    <i className="ri-add-line"></i>
                    Nouvelle commande
                </button>
            </div>

            <div className="relative">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                    type="text"
                    placeholder="Rechercher par code ou adresse..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
            </div>

            {loading ? (
                <div className="text-center py-12">
                    <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin mx-auto block"></i>
                    <p className="mt-3 text-gray-500">Chargement de vos commandes...</p>
                </div>
            ) : filteredOrders.length === 0 ? (
                <div className="bg-white rounded-2xl p-12 text-center">
                    <i className="ri-inbox-line text-5xl text-gray-300 mx-auto block"></i>
                    <p className="mt-4 text-gray-500">Aucune commande trouvée</p>
                </div>
            ) : (
                <div className="grid gap-4">
                    {filteredOrders.map((order) => (
                        <div key={order.id} className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-md transition-all">
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="font-semibold text-lg">#{order.code_suivi}</p>
                                    <p className="text-sm text-gray-500 mt-1">{order.type_livraison}</p>
                                </div>
                                <span className={`px-4 py-1.5 rounded-full text-sm font-medium ${order.statut === 'livree' ? 'bg-green-100 text-green-700' :
                                        order.statut === 'en_transit' || order.statut === 'en_cours' ? 'bg-blue-100 text-blue-700' :
                                            'bg-yellow-100 text-yellow-700'
                                    }`}>
                                    {order.statut}
                                </span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                                <div>
                                    <p className="text-xs text-gray-500">Ramassage</p>
                                    <p className="font-medium">{order.adresse_ramassage}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Livraison</p>
                                    <p className="font-medium">{order.adresse_livraison}</p>
                                </div>
                            </div>

                            <div className="flex justify-between items-center mt-6 pt-4 border-t">
                                <p className="font-semibold text-teal-600">
                                    {order.prix_estime?.toLocaleString() || '—'} GNF
                                </p>
                                <button
                                    onClick={() => onTrackOrder(order)}
                                    className="px-5 py-2 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors flex items-center gap-2"
                                >
                                    <i className="ri-map-pin-line"></i>
                                    Suivre
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}